import {
  Pencil,
  Eye,
  Trash2,
  Circle,
  CircleAlert,
  CircleDollarSign,
  SquarePen,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { cn } from "../../lib/utils";

const IMAGE_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const cardHoverClass =
  // "bg-white dark:bg-card p-3 lg:p-5 rounded-2xl border border-[#E8DDD3] dark:border-border shadow-sm " +
  "bg-white dark:bg-card rounded-2xl border border-[#E8DDD3] dark:border-border shadow-sm " +
  "transition-all duration-300 ease-in-out " +
  "hover:border-[#e8d0aa] dark:hover:border-primary/50 hover:shadow-[5px_5px_0px_0px_#e8d0aa] dark:hover:shadow-[5px_5px_0px_0px_rgba(232,208,170,0.2)] hover:-translate-y-1 " +
  "hover:scale-[1.00] cursor-pointer relative overflow-hidden";

const StatusBadge = ({ status }) => {
  const styles = {
    Available:
      "bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-100 dark:border-green-800/50",
    "On Rent":
      "bg-orange-100 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400 border border-orange-100 dark:border-orange-800/50",
    "Under Maintenance":
      "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-800/50",
  };

  const icons = {
    Available: <Circle className="w-2.5 h-2.5 flex-shrink-0" />,
    "On Rent": <CircleAlert className="w-2.5 h-2.5 flex-shrink-0" />,
    "Under Maintenance": <Circle className="w-2.5 h-2.5 flex-shrink-0" />,
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 ${
        styles[status] || ""
      }`}
    >
      {icons[status] || <Circle className="w-2.5 h-2.5 flex-shrink-0" />}
      <span>{status}</span>
    </span>
  );
};

const EquipmentRentRequestCard = ({
  item,
  handleOpenEquipmentDetails,
}) => {
  const { theme } = useTheme();

  // Mapping New API Fields
  const equipmentId = item.equipment_id;
  const equipmentName = item.equipment_name || "Unknown Equipment";
  const description = item.description || "No description available";
  const marketPrice = item.purchase_price || 0;
  const rentalPrice = item.daily_rental_rate || 0;
  const manufacturer = item.manufacturer || "Equipment"; // Using manufacturer as category tag

  // Photo Logic: equipment_photos -> file_url
  const hasPhotos = item.equipment_photos && item.equipment_photos.length > 0;
  const displayImage = hasPhotos
    ? `${IMAGE_BASE_URL}${item.equipment_photos[0].file_url}`
    : "/images/misc/equipment_1.png";

  // Status mapping based on initial_status_id
  const statusMap = {
    1: "Available",
    2: "On Rent",
    3: "Under Maintenance",
  };
  const status = statusMap[item.initial_status_id] || "Available";

  return (
    <div
      key={equipmentId}
      className={cn(
        cardHoverClass,
        theme === "dark"
          ? "bg-card"
          // : "bg-gradient-to-b from-[#E7DDD4] to-[#FFFFFF] bg-no-repeat bg-cover"
          :"bg-[linear-gradient(0deg,#FFF_40%,#E7DDD4_100%)] bg-[#D3D3D3]"
      )}
    >
      {/* Image Container */}
      {/* <div className="relative overflow-hidden group h-[196px] bg-[#E7DDD4]"> */}
      <div className="relative overflow-hidden group h-[196px] bg-transparent">
        {/* Tag (Category/Manufacturer) */}
        <span className="absolute top-3 left-3 bg-white dark:bg-background text-black dark:text-foreground text-sm font-medium px-3 py-1 rounded-full shadow-sm z-20">
          {manufacturer}
        </span>

        <img
          src={displayImage}
          alt={equipmentName}
          onError={(e) => {
            e.target.src = "/images/misc/equipment_1.png";
          }}
          className="mx-auto h-full w-full object-contain transition-transform duration-300 ease-out group-hover:scale-110 relative z-10"
        />

        {/* Bottom Fade Overlay */}
        {/* <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[60%] z-20"
          style={{
            background:
              theme === "dark"
                ? "linear-gradient(182deg, rgba(29, 29, 27, 0.00) 8.65%, #1D1D1B 98.14%)"
                : "linear-gradient(182deg, rgba(255, 255, 255, 0.00) 8.65%, #FFFFFF 98.14%)",
          }}
        /> */}
      </div>

      {/* Content */}
      {/* <div className="px-6 py-2 bg-white dark:bg-card"> */}
      <div className="mt-2 px-6 py-2 bg-transparent">
        <div className="flex items-start justify-between gap-6">
          <h3 className="font-semibold text-base leading-6 truncate w-2/3 text-gray-900 dark:text-foreground">
            {equipmentName}
          </h3>
          <StatusBadge status={status} />
        </div>

        <p className="text-sm text-[#737373] dark:text-muted-foreground mt-1 leading-5 line-clamp-1 min-w-0">
          {description}
        </p>

        <div className="mt-2 text-sm font-medium text-[#1F2937] dark:text-foreground">
          <div className="flex justify-between items-center">
             <span className="text-xs text-gray-500">Market Price:</span>
             <span>${marketPrice}</span>
          </div>
          {/* <div className="flex justify-between items-center text-xs text-gray-400 mt-0.5">
             <span>Qty Available:</span>
             <span>{item.quantity || 1}</span>
          </div> */}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 xl:px-6 xl:py-4 border-t border-[#F5EBDA] dark:border-border flex flex-wrap items-center justify-between bg-white dark:bg-card">
        <div className="flex items-center gap-2 font-bold text-sm text-gray-900 dark:text-foreground">
          <CircleDollarSign className="w-4 h-4 text-primary" />
          <span>
            ${rentalPrice}<span className="text-[10px] font-normal text-gray-500">/day</span>
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mt-2 xl:mt-0">
          <button
            onClick={() => handleOpenEquipmentDetails(equipmentId)}
            className="w-9 h-9 rounded-lg bg-[#F5F1EE] dark:bg-background flex items-center justify-center shadow transition-colors hover:bg-[#e8d0aa]/30 dark:hover:bg-primary/20 group"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-[#6B4F2A] dark:text-foreground group-hover:text-[#6B4F2A] dark:group-hover:text-primary" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentRentRequestCard;
